import { makeStyles, tokens, Text } from '@coreai/fluentui-react';
import { CheckmarkFilled } from '@fluentui/react-icons';
import { MOBILE_BREAKPOINT } from './constants';
import { ENTERPRISE_CARDS } from './data';
import { ottoTokens } from '@/theme/ottoTokens';

const useStyles = makeStyles({
  section: {
    paddingBlock: '100px',
    borderTopStyle: 'solid',
    borderTopWidth: '1px',
    borderTopColor: tokens.colorNeutralStroke2,
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      paddingBlock: '64px',
    },
  },
  inner: {
    maxWidth: '1200px',
    marginInline: 'auto',
    paddingInline: tokens.spacingHorizontalXXL,
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      paddingInline: tokens.spacingHorizontalL,
    },
  },
  head: {
    textAlign: 'center',
    maxWidth: '760px',
    marginInline: 'auto',
    marginBlockEnd: '48px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  eyebrow: {
    display: 'block',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: ottoTokens.landingAccentTextCool,
  },
  heading: {
    display: 'block',
    fontSize: 'clamp(28px, 4vw, 44px)',
    fontWeight: tokens.fontWeightBold,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
    color: tokens.colorNeutralForeground1,
  },
  lead: {
    fontSize: tokens.fontSizeBase400,
    lineHeight: 1.55,
    color: tokens.colorNeutralForeground2,
    maxWidth: '700px',
    marginInline: 'auto',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: tokens.spacingHorizontalL,
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      gridTemplateColumns: '1fr',
    },
  },
  card: {
    padding: tokens.spacingVerticalXXL,
    backgroundColor: ottoTokens.landingCardBackground,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  cardTitle: {
    display: 'block',
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  list: {
    listStyleType: 'none',
    paddingInlineStart: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  item: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: 1.6,
    color: tokens.colorNeutralForeground2,
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
  },
  check: {
    color: ottoTokens.statusSucceeded,
    fontSize: tokens.fontSizeBase400,
    flexShrink: 0,
    marginBlockStart: '2px',
  },
});

export const EnterpriseGridSection = () => {
  const styles = useStyles();

  return (
    <section id="enterprise" className={styles.section} aria-labelledby="enterprise-title">
      <div className={styles.inner}>
        <div className={styles.head}>
          <Text className={styles.eyebrow}>Enterprise ready, on day one</Text>
          <Text as="h2" id="enterprise-title" className={styles.heading}>
            Governance is the distance between a demo and production.
          </Text>
          <Text className={styles.lead}>
            Every team has an AI agent demo. Few have one in production. Logic Apps Automation
            closes that gap with the security, identity, networking, and observability you need to
            ship — built in, not bolted on.
          </Text>
        </div>
        <div className={styles.grid}>
          {ENTERPRISE_CARDS.map((card) => (
            <div key={card.title} className={styles.card}>
              <Text as="h3" className={styles.cardTitle}>{card.title}</Text>
              <ul className={styles.list}>
                {card.items.map((item) => (
                  <li key={item} className={styles.item}>
                    <CheckmarkFilled className={styles.check} aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
